import React, { useMemo, forwardRef } from 'react';
import { Card, Box, Typography } from '@mui/material';
import { COLOR, FUENTE } from './tokens';
import InfoGrafico from './InfoGrafico';
import { MOCK_CANAL_ORIGEN } from './mockDataEjemplos';

/**
 * Canal de origen — tabla en vez de dona: la dona y el texto lateral mostraban lo
 * mismo dos veces (composición) y dejaban el ausentismo aparte, en texto. Acá
 * Turnos, % del total y Ausentismo conviven en una sola fila por canal.
 * @param {{data: Array<{nombreCanal: string, total: number, noAsistio: number}>, compacto?: boolean}} props
 */
const TablaCanalOrigen = forwardRef(({ data, compacto = false }, ref) => {
    const filas = useMemo(() => [...(data || [])].sort((a, b) => b.total - a.total), [data]);
    const total = useMemo(() => filas.reduce((acc, f) => acc + f.total, 0), [filas]);

    return (
        <Card ref={ref} variant="outlined" sx={{
            position: compacto ? 'static' : 'relative', border: compacto ? 'none' : '1px solid #e2e8f0',
            borderRadius: compacto ? 0 : '12px', boxShadow: 'none', padding: compacto ? 0 : '20px 22px',
        }}>
            {!compacto && (
                <>
                    <InfoGrafico
                        titulo="Canal de origen"
                        queMuestra="Te dice de dónde vino la demanda del período (qué canal de marketing trajo más turnos) y, al lado, qué tan bien se presentaron los clientes que llegaron por cada uno."
                        ejemploVisual={<TablaCanalOrigen compacto data={MOCK_CANAL_ORIGEN} />}
                        comoLeerlo={[
                            'Cada fila es un canal del catálogo de referencias (de dónde dijo el cliente que venía), ordenado de mayor a menor volumen.',
                            '"Turnos" es la cantidad de turnos que entraron por ese canal en el período, en cualquier estado y sin contar sobreturnos (quien viene sin turno no tenía una cita a la que faltar). "Resueltos" son los que vinieron o faltaron: sin cancelados ni los que siguen abiertos.',
                            '"% del total" es la participación de ese canal sobre el total de turnos con canal registrado.',
                            '"Ausentismo" es no vinieron ÷ resueltos: el mismo número del gráfico "Ausentismo por canal" (pestaña Resultados) y de la Asistencia del encabezado (que es su complemento). No tiene color propio acá; para compararlo contra el promedio y ver semáforo, está ese gráfico.',
                        ]}
                        analisis="Facebook Bot trae bastante menos volumen que Instagram (25 contra 40), pero su ausentismo es el más alto de los tres (40% de esos turnos no se presentó). Volumen y calidad de la demanda son cosas distintas: antes de invertir más en el canal que más turnos trae, conviene mirar cuál trae los turnos que realmente se cumplen."
                    />
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 16, fontWeight: 700, color: COLOR.negro }}>Canal de origen</Typography>
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 12.5, color: COLOR.grisTexto, marginBottom: '14px' }}>
                        De dónde vinieron los turnos del período (catálogo de referencias).
                    </Typography>
                </>
            )}
            {total === 0 ? (
                <Typography sx={{ fontFamily: FUENTE, fontSize: 13, color: COLOR.grisTexto }}>
                    No hay turnos con canal registrado en el período.
                </Typography>
            ) : (
                <>
                <Box sx={{ overflowX: 'auto' }}>
                    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontFamily: FUENTE }}>
                        <Box component="thead">
                            <Box component="tr">
                                {['Canal', 'Turnos', 'Resueltos', '% del total', 'Ausentismo'].map((h) => (
                                    <Box component="th" key={h} sx={{
                                        textAlign: h === 'Canal' ? 'left' : 'right', padding: '8px 10px',
                                        fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
                                        color: COLOR.grisTexto, borderBottom: '1px solid #e2e8f0',
                                    }}>
                                        {h}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                        <Box component="tbody">
                            {filas.map((f) => {
                                const pct = total ? (f.total / total) * 100 : 0;
                                // Los que vinieron o faltaron: el denominador real del ausentismo. Sin ese
                                // dato (los ejemplos de la ayuda) cae al total del canal.
                                const resueltos = f.resueltos ?? f.total;
                                // El % lo calcula el backend con la misma regla del encabezado y del gráfico
                                // "Ausentismo por canal": nunca se recalcula acá contra otro denominador.
                                const ausentismo = f.tasaAusentismo != null
                                    ? Number(f.tasaAusentismo)
                                    : (resueltos ? (f.noAsistio / resueltos) * 100 : 0);
                                return (
                                    <Box component="tr" key={f.nombreCanal}>
                                        <Celda align="left">{f.nombreCanal}</Celda>
                                        <Celda>{f.total.toLocaleString('es-AR')}</Celda>
                                        <Celda>{resueltos.toLocaleString('es-AR')}</Celda>
                                        <Celda>{pct.toFixed(1)}%</Celda>
                                        <Celda>{ausentismo.toFixed(1)}%</Celda>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                </Box>
                {!compacto && (
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 11.5, color: COLOR.grisTexto, marginTop: '10px' }}>
                        Ausentismo = no vinieron ÷ resueltos (vinieron o faltaron, sin cancelados ni sobreturnos): la misma base del
                        encabezado y del gráfico "Ausentismo por canal".
                    </Typography>
                )}
                </>
            )}
        </Card>
    );
});

const Celda = ({ children, align = 'right' }) => (
    <Box component="td" sx={{
        padding: '8px 10px', textAlign: align, borderBottom: '1px solid #F1F5F9',
        fontSize: 13, color: COLOR.negro, fontVariantNumeric: 'tabular-nums',
    }}>
        {children}
    </Box>
);

export default TablaCanalOrigen;
